<script>
import { mapGetters, mapActions } from "vuex";
import axios from "axios";
import adminApplicationsList from "../../components/AdminApplicationsList.vue";
import { loadListView } from "@/utils/applications-list-view";

export default {
  components: { adminApplicationsList },

  data: (vm) => ({
    // restore the tab the user left on (paired with AdminApplicationsList view persistence)
    tab: ((saved) => (saved && saved.tab != null ? saved.tab : (Number(vm.$route.query.tab) || 0)))(loadListView()),
    isLoading: false,
    canCreate: false,
    canUpdate: false,
    canDelete: false,
    canExport: false,
    canRead: false,
    countsLoaded: false,
    growthLoaded: false,
    // Drives the source filter on <admin-applications-list :source>. MUST start at "ALL" and NOT be
    // restored from localStorage — a persisted source (e.g. ADMISSION, ~2 rows) would stay applied on
    // reload and hide almost every applicant ("show all" regression). The toggle is an opt-in filter only.
    chartSource: "ALL",
    chartDays: 90,
    tooltip: { show: false, x: 0, y: 0, px: 0, py: 0, date: "", count: 0, admission: 0, qabul: 0, lead: 0, points: [] },
    crosshair: { show: false, x: 0, y: 0 },
    chartType: "area",
    chartCollapsed: localStorage.getItem("chartCollapsed") === "true",
  }),

  created() {
    this.INIT_COUNT().finally(() => {
      this.countsLoaded = true;
      // The counter pills only render once the counts land, so three tabs get wider AFTER
      // v-tabs measured them. Its ResizeObserver watches the strip — whose size does not
      // change — not the tab, so the active tab's underline would keep its old, too-short
      // width (it stopped before the pill) until something else forced a re-measure.
      this.$nextTick(() => { if (this.$refs.tabsBar) this.$refs.tabsBar.callSlider(); });
    });
    this.INIT_GROWTH({ days: this.chartDays }).finally(() => { this.growthLoaded = true; });
  },

  async mounted() {
    this.canRead = await this.hasRole("ARIZALAR", "READ");
    this.canCreate = await this.hasRole("ARIZALAR", "CREATE");
    this.canUpdate = await this.hasRole("ARIZALAR", "UPDATE");
    this.canDelete = await this.hasRole("ARIZALAR", "DELETE");
    this.canExport = await this.hasRole("ARIZALAR", "EXCEL");
  },

  computed: {
    ...mapGetters("admin-applications", ["GET_COUNT", "GET_GROWTH"]),
    ...mapGetters("auth", ["GET_USER"]),
    user: (vm) => vm.GET_USER,
    isSupperAdmin: (vm) => vm.user.role == "ADMIN_SUPER",
    totalAll() { return this.GET_COUNT.ALL || 1; },

    chartData() {
      const raw = this.GET_GROWTH || [];
      const EMPTY = { ADMISSION: 0, QABUL: 0, LEAD: 0, REFERRAL: 0, CALL_MARKAZ: 0 };
      // REFERRAL / CALL_MARKAZ are overlapping series (a LEAD applicant can also be a referral),
      // emitted by the backend as extra synthetic "source" rows. They live in their own buckets,
      // are never folded into ADMISSION, and are never added to `total`.
      const KNOWN = ["QABUL", "LEAD", "REFERRAL", "CALL_MARKAZ"];
      const byDate = {};
      raw.forEach((r) => {
        if (!byDate[r.date]) byDate[r.date] = { ...EMPTY };
        const src = (r.source || "ADMISSION").toUpperCase();
        const key = KNOWN.includes(src) ? src : "ADMISSION";
        byDate[r.date][key] += r.count;
      });
      const dates = [];
      const now = new Date();
      for (let i = this.chartDays - 1; i >= 0; i--) {
        const d = new Date(now); d.setDate(d.getDate() - i);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        dates.push(`${yyyy}-${mm}-${dd}`);
      }
      return dates.map((date) => {
        const e = byDate[date] || EMPTY;
        return {
          date,
          admission: e.ADMISSION,
          qabul: e.QABUL,
          lead: e.LEAD,
          referral: e.REFERRAL,
          callMarkaz: e.CALL_MARKAZ,
          total: e.ADMISSION + e.QABUL + e.LEAD,
        };
      });
    },

    cumulativeData() {
      let sumA = 0, sumQ = 0, sumL = 0, sumR = 0, sumC = 0;
      return this.chartData.map((d) => {
        sumA += d.admission;
        sumQ += d.qabul;
        sumL += d.lead;
        sumR += d.referral;
        sumC += d.callMarkaz;
        return { ...d, cumAdmission: sumA, cumQabul: sumQ, cumLead: sumL, cumReferral: sumR, cumCallMarkaz: sumC, cumTotal: sumA + sumQ + sumL };
      });
    },

    seriesMax() {
      const data = this.cumulativeData;
      if (!data.length) return 1;
      let max = 0;
      if (this.chartSource === "ALL") {
        // ALL plots 3 separate lines — fit the axis to the tallest line, not their sum.
        data.forEach((d) => { max = Math.max(max, d.cumAdmission, d.cumQabul, d.cumLead); });
      } else {
        const key = {
          ADMISSION: "cumAdmission", QABUL: "cumQabul", LEAD: "cumLead",
          REFERRAL: "cumReferral", CALL_MARKAZ: "cumCallMarkaz",
        }[this.chartSource] || "cumQabul";
        data.forEach((d) => { max = Math.max(max, d[key]); });
      }
      return Math.max(1, max);
    },

    // Symlog only when the ALL view spans >= 2 decades; single-source stays linear (more intuitive).
    useLog() {
      return this.chartSource === "ALL" && this.seriesMax >= 100;
    },

    // Transformed denominator incl. headroom (mode-aware): log -> ~0.2 decade; linear -> 8%.
    tSpan() {
      if (this.useLog) return (this.valueT(this.seriesMax) || 1) + 0.2;
      return this.seriesMax * 1.08;
    },

    chartTotal() { return this.chartData.reduce((s, d) => s + d.total, 0); },
    admissionTotal() { return this.chartData.reduce((s, d) => s + d.admission, 0); },
    qabulTotal() { return this.chartData.reduce((s, d) => s + d.qabul, 0); },
    leadTotal() { return this.chartData.reduce((s, d) => s + d.lead, 0); },
    referralTotal() { return this.chartData.reduce((s, d) => s + d.referral, 0); },
    callMarkazTotal() { return this.chartData.reduce((s, d) => s + d.callMarkaz, 0); },
    hasQabulData() { return this.qabulTotal > 0; },
    hasAdmissionData() { return this.admissionTotal > 0; },
    hasLeadData() { return this.leadTotal > 0; },

    displayTotal() {
      if (this.chartSource === "ADMISSION") return this.admissionTotal;
      if (this.chartSource === "QABUL") return this.qabulTotal;
      if (this.chartSource === "LEAD") return this.leadTotal;
      if (this.chartSource === "REFERRAL") return this.referralTotal;
      if (this.chartSource === "CALL_MARKAZ") return this.callMarkazTotal;
      return this.chartTotal;
    },

    todayCount() {
      const d = this.chartData;
      if (!d.length) return 0;
      const last = d[d.length - 1];
      if (this.chartSource === "ADMISSION") return last.admission;
      if (this.chartSource === "QABUL") return last.qabul;
      if (this.chartSource === "LEAD") return last.lead;
      if (this.chartSource === "REFERRAL") return last.referral;
      if (this.chartSource === "CALL_MARKAZ") return last.callMarkaz;
      return last.total;
    },

    yesterdayCount() {
      const d = this.chartData;
      if (d.length < 2) return 0;
      const prev = d[d.length - 2];
      if (this.chartSource === "ADMISSION") return prev.admission;
      if (this.chartSource === "QABUL") return prev.qabul;
      if (this.chartSource === "LEAD") return prev.lead;
      if (this.chartSource === "REFERRAL") return prev.referral;
      if (this.chartSource === "CALL_MARKAZ") return prev.callMarkaz;
      return prev.total;
    },

    deltaAbs() { return this.todayCount - this.yesterdayCount; },
    isUp() { return this.deltaAbs >= 0; },

    /* Show % only when previous > 0 to avoid misleading "∞%" or "+100%" from 0→1 */
    deltaPercent() {
      if (!this.yesterdayCount) return null;
      return (((this.todayCount - this.yesterdayCount) / this.yesterdayCount) * 100).toFixed(1);
    },

    /* Last cumulative value */
    lastValue() {
      const d = this.cumulativeData;
      if (!d.length) return 0;
      const last = d[d.length - 1];
      if (this.chartSource === "ADMISSION") return last.cumAdmission;
      if (this.chartSource === "QABUL") return last.cumQabul;
      if (this.chartSource === "LEAD") return last.cumLead;
      if (this.chartSource === "REFERRAL") return last.cumReferral;
      if (this.chartSource === "CALL_MARKAZ") return last.cumCallMarkaz;
      return last.cumTotal;
    },

    /* SVG geometry */
    W() { return 960; },
    H() { return 300; },
    PL() { return 12; },
    PR() { return 62; },
    PT() { return 16; },
    PB() { return 30; },
    pW() { return this.W - this.PL - this.PR; },
    pH() { return this.H - this.PT - this.PB; },

    yTicks() {
      if (this.useLog) {
        // Decade ticks 0,1,10,100,1k,10k... — evenly spaced in pixels, an explicit x10 ruler.
        const max = this.seriesMax;
        const t = [0];
        for (let p = 0; Math.pow(10, p) <= max; p++) t.push(Math.pow(10, p));
        return t.map((v) => ({ v, y: this.yScale(v), label: this.fmtTick(v) }));
      }
      const max = this.seriesMax;
      const step = Math.max(1, this.niceNum(max / 5, true));
      const t = [];
      for (let v = 0; v <= max + 1e-9; v += step) t.push(v);
      return t.map((v) => ({ v, y: this.yScale(v), label: this.fmtTick(v) }));
    },

    xLabels() {
      const data = this.cumulativeData;
      if (!data.length) return [];
      const step = Math.max(1, Math.floor(data.length / 8));
      const labels = [];
      for (let i = 0; i < data.length; i += step) {
        labels.push({
          i,
          label: this.fmtDateShort(data[i].date),
          x: this.PL + (i / (data.length - 1 || 1)) * this.pW,
        });
      }
      return labels;
    },

    admissionPath() { return this.buildSmoothPath("cumAdmission"); },
    qabulPath() { return this.buildSmoothPath("cumQabul"); },
    leadPath() { return this.buildSmoothPath("cumLead"); },
    referralPath() { return this.buildSmoothPath("cumReferral"); },
    callMarkazPath() { return this.buildSmoothPath("cumCallMarkaz"); },

    admissionArea() { return this.buildSmoothArea("cumAdmission"); },
    qabulArea() { return this.buildSmoothArea("cumQabul"); },
    leadArea() { return this.buildSmoothArea("cumLead"); },
    referralArea() { return this.buildSmoothArea("cumReferral"); },
    callMarkazArea() { return this.buildSmoothArea("cumCallMarkaz"); },

    lastPoint() {
      const data = this.cumulativeData;
      if (!data.length) return { x: 0, y: 0 };
      const last = data[data.length - 1];
      let val = last.cumTotal;
      if (this.chartSource === "ADMISSION") val = last.cumAdmission;
      else if (this.chartSource === "QABUL") val = last.cumQabul;
      else if (this.chartSource === "LEAD") val = last.cumLead;
      else if (this.chartSource === "REFERRAL") val = last.cumReferral;
      else if (this.chartSource === "CALL_MARKAZ") val = last.cumCallMarkaz;
      return {
        x: this.PL + this.pW,
        y: this.yScale(val),
      };
    },

    /* Per-series true-value pills at the right edge (symlog compresses magnitude,
       so show the real numbers). Up to 3 in ALL, exactly 1 in single-source. */
    endLabels() {
      const data = this.cumulativeData;
      if (!data.length) return [];
      const last = data[data.length - 1];
      const C = { cumAdmission: "#2962ff", cumQabul: "#00c853", cumLead: "#f59e0b",
                  cumReferral: "#7c3aed", cumCallMarkaz: "#0d9488" };
      let keys;
      if (this.chartSource === "ALL") {
        keys = ["cumAdmission"];
        if (this.hasQabulData) keys.push("cumQabul");
        if (this.hasLeadData) keys.push("cumLead");
      } else {
        keys = [{ ADMISSION: "cumAdmission", QABUL: "cumQabul", LEAD: "cumLead",
                  REFERRAL: "cumReferral", CALL_MARKAZ: "cumCallMarkaz" }[this.chartSource]];
      }
      // readable pill text: dark on light fills (amber/green), white on dark fills (blue/purple/teal)
      const lum = (hex) => { const m = parseInt(hex.slice(1), 16); return 0.2126 * ((m >> 16) & 255) + 0.7152 * ((m >> 8) & 255) + 0.0722 * (m & 255); };
      const items = keys.map((k) => ({
        key: k, color: C[k], txt: lum(C[k]) > 130 ? "#1a2027" : "#ffffff",
        value: last[k], dotY: this.yScale(last[k]), x: this.PL + this.pW,
      })).sort((a, b) => a.dotY - b.dotY);
      const GAP = 19; let prev = -Infinity;
      items.forEach((it) => { it.labelY = Math.max(it.dotY, prev + GAP); prev = it.labelY; });
      // account for the pill rect's lower half (spans labelY-10..labelY+10) so it never crosses the baseline
      const overflow = items.length ? (items[items.length - 1].labelY + 10) - (this.PT + this.pH) : 0;
      if (overflow > 0) items.forEach((it) => { it.labelY -= overflow; });
      if (items.length) items[0].labelY = Math.max(items[0].labelY, this.PT + 10);
      return items;
    },

    // y positions of the end pills, so y-axis tick labels they would cover can be hidden
    endLabelYs() { return this.endLabels.map((e) => e.labelY); },

    primaryColor() {
      if (this.chartSource === "QABUL") return "#00c853";
      if (this.chartSource === "LEAD") return "#f59e0b";
      if (this.chartSource === "REFERRAL") return "#7c3aed";
      if (this.chartSource === "CALL_MARKAZ") return "#0d9488";
      return "#2962ff";
    },

    tipStyle() {
      const tipW = 180; // approximate tooltip width
      let left = this.tooltip.px;
      const top = this.tooltip.py;

      // Clamp left so tooltip doesn't go off-screen on either side
      left = Math.max(tipW / 2 + 4, left);

      // If data point is near the top, show tooltip below; otherwise above
      const showBelow = top < 100;

      if (showBelow) {
        return { left: left + 'px', top: (top + 16) + 'px', transform: 'translateX(-50%)' };
      }
      return { left: left + 'px', top: (top - 8) + 'px', transform: 'translate(-50%, -100%)' };
    },
  },

  methods: {
    ...mapActions("admin-applications", ["INIT_COUNT", "INIT_GROWTH"]),

    pct(val) { return ((val || 0) / (this.totalAll || 1) * 100).toFixed(0) + '%'; },
    // Session-only: intentionally NOT persisted, so every page load starts at "ALL" and shows all
    // applicants. The source toggle stays a non-sticky, opt-in filter for the current view only.
    setChartSource(src) { this.chartSource = src; },
    toggleChartCollapsed() {
      this.chartCollapsed = !this.chartCollapsed;
      localStorage.setItem("chartCollapsed", this.chartCollapsed);
    },

    setChartDays(days) {
      this.chartDays = days;
      this.growthLoaded = false;
      this.INIT_GROWTH({ days }).finally(() => { this.growthLoaded = true; });
    },

    /* ---------- y-scale: THE single source of truth for every y coordinate ---------- */
    // forward value -> "scale units": identity for linear, symmetric-log for the ALL view.
    // sign() form so a future negative (refund) cumulative can never break log().
    valueT(v) {
      if (!this.useLog) return v;
      const s = Math.sign(v), a = Math.abs(v);
      return s * (a <= 1 ? a : 1 + Math.log10(a)); // 0->0, 1->1, 10->2, 100->3, 1k->4, 10k->5
    },
    yScale(v) {
      return this.PT + this.pH - (this.valueT(v) / this.tSpan) * this.pH;
      // yScale(0) === PT + pH exactly -> area baseline + value-0 line align by construction.
    },
    niceNum(x, round) {
      if (x <= 0) return 1;
      const e = Math.floor(Math.log10(x));
      const f = x / Math.pow(10, e);
      const nf = round ? (f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10)
                       : (f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10);
      return nf * Math.pow(10, e);
    },
    fmtTick(v) {
      v = Math.round(v);
      if (v >= 1e6) return (v / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
      if (v >= 1e3) return (v / 1e3).toFixed(1).replace(/\.0$/, "") + "k";
      return this.formatNum(v);
    },

    // Fritsch-Carlson monotone cubic on (x, yScale(value)) — no overshoot on the cumulative cliff.
    buildSmoothPath(key) {
      const data = this.cumulativeData;
      const n = data.length;
      if (!n) return "";
      const xs = [], ys = [];
      for (let i = 0; i < n; i++) {
        xs.push(this.PL + (i / (n - 1 || 1)) * this.pW);
        ys.push(this.yScale(data[i][key])); // transform FIRST, interpolate second
      }
      if (n === 1) return `M${xs[0].toFixed(1)},${ys[0].toFixed(1)}`;

      const sl = new Array(n - 1); // secant slopes
      for (let i = 0; i < n - 1; i++) sl[i] = (ys[i + 1] - ys[i]) / (xs[i + 1] - xs[i]);

      const m = new Array(n); // tangents
      m[0] = sl[0]; m[n - 1] = sl[n - 2];
      for (let i = 1; i < n - 1; i++)
        m[i] = sl[i - 1] * sl[i] <= 0 ? 0 : (sl[i - 1] + sl[i]) / 2; // 0 at flats -> kills wobble

      for (let i = 0; i < n - 1; i++) { // monotonicity clamp (no overshoot)
        if (sl[i] === 0) { m[i] = 0; m[i + 1] = 0; continue; }
        const a = m[i] / sl[i], b = m[i + 1] / sl[i], h = a * a + b * b;
        if (h > 9) { const tau = 3 / Math.sqrt(h); m[i] = tau * a * sl[i]; m[i + 1] = tau * b * sl[i]; }
      }

      let path = `M${xs[0].toFixed(1)},${ys[0].toFixed(1)}`;
      for (let i = 0; i < n - 1; i++) {
        const h = xs[i + 1] - xs[i];
        const c1x = xs[i] + h / 3, c1y = ys[i] + (m[i] * h) / 3;
        const c2x = xs[i + 1] - h / 3, c2y = ys[i + 1] - (m[i + 1] * h) / 3;
        path += ` C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${xs[i + 1].toFixed(1)},${ys[i + 1].toFixed(1)}`;
      }
      return path;
    },

    buildSmoothArea(key) {
      const path = this.buildSmoothPath(key);
      if (!path) return "";
      const base = this.yScale(0); // == PT + pH (baseline) by construction
      return `${path} L${this.PL + this.pW},${base} L${this.PL},${base} Z`;
    },

    onChartMove(evt) {
      const svg = this.$refs.chartSvg;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const mx = (evt.clientX - rect.left) * (this.W / rect.width);
      const my = (evt.clientY - rect.top) * (this.H / rect.height);

      this.crosshair = { show: true, x: mx, y: my };

      const data = this.cumulativeData;
      if (!data.length) return;
      const idx = Math.round(((mx - this.PL) / this.pW) * (data.length - 1));
      const ci = Math.max(0, Math.min(data.length - 1, idx));
      const d = data[ci];
      let val = d.cumTotal;
      if (this.chartSource === "ADMISSION") val = d.cumAdmission;
      else if (this.chartSource === "QABUL") val = d.cumQabul;
      else if (this.chartSource === "LEAD") val = d.cumLead;
      else if (this.chartSource === "REFERRAL") val = d.cumReferral;
      else if (this.chartSource === "CALL_MARKAZ") val = d.cumCallMarkaz;

      const px = this.PL + (ci / (data.length - 1 || 1)) * this.pW;
      const py = this.yScale(val);

      /* Compute pixel position relative to .tv for the floating tooltip */
      const tvEl = svg.closest('.tv');
      const tvRect = tvEl ? tvEl.getBoundingClientRect() : rect;
      const pxLeft = rect.left + (px / this.W) * rect.width - tvRect.left;
      const pxTop = rect.top + (py / this.H) * rect.height - tvRect.top;

      const SC = { cumAdmission: "#2962ff", cumQabul: "#00c853", cumLead: "#f59e0b", cumReferral: "#7c3aed", cumCallMarkaz: "#0d9488" };
      let pk;
      if (this.chartSource === "ALL") {
        pk = ["cumAdmission"];
        if (this.hasQabulData) pk.push("cumQabul");
        if (this.hasLeadData) pk.push("cumLead");
      } else {
        pk = [{ ADMISSION: "cumAdmission", QABUL: "cumQabul", LEAD: "cumLead", REFERRAL: "cumReferral", CALL_MARKAZ: "cumCallMarkaz" }[this.chartSource]];
      }
      const points = pk.map((k) => ({ color: SC[k], y: this.yScale(d[k]) }));

      this.tooltip = {
        show: true, x: px, y: py, points,
        px: pxLeft, py: pxTop,
        date: d.date,
        count: val,
        admission: d.cumAdmission,
        qabul: d.cumQabul,
        lead: d.cumLead,
        dayAdmission: d.admission,
        dayQabul: d.qabul,
        dayLead: d.lead,
        dayTotal: d.total,
      };
    },

    onChartLeave() {
      this.crosshair.show = false;
      this.tooltip.show = false;
    },

    fmtDate(d) {
      if (!d) return "";
      const p = d.split("-");
      const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      return `${months[parseInt(p[1])-1]} ${parseInt(p[2])}, ${p[0]}`;
    },

    fmtDateShort(d) {
      if (!d) return "";
      const p = d.split("-");
      const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      return `${months[parseInt(p[1])-1]} ${parseInt(p[2])}`;
    },

    formatNum(n) {
      return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    async hasRole(panel, op) {
      try {
        const res = await axios.get(`/admin/panel-permissions/check`, {
          params: { appPanel: panel, op }
        });
        return res.data === true;
      } catch {
        return false;
      }
    },

  },
};
</script>

<template>
  <div class="ux-applications">
    <!-- ── Royal Stat Cards ── -->
    <v-container v-if="isSupperAdmin || canRead" fluid class="royal-row">
      <div class="royal-grid" role="group" aria-label="Application stats">
        <v-card class="royal-card royal--all" elevation="0" :ripple="true" tabindex="0">
          <template v-if="countsLoaded">
            <div class="royal-title" v-text="$t('All applications')" />
            <div class="royal-count">{{ GET_COUNT.ALL | sum }}</div>
            <div class="royal-meter"><span :style="{ width: '100%' }"></span></div>
          </template>
          <template v-else><div class="sk sk--title"></div><div class="sk sk--count"></div><div class="sk sk--meter"></div></template>
        </v-card>
        <v-card class="royal-card royal--dtm" elevation="0" :ripple="true" tabindex="0">
          <template v-if="countsLoaded">
            <div class="royal-title">DTM</div>
            <div class="royal-count">{{ GET_COUNT.DTM | sum }}</div>
            <div class="royal-meter"><span :style="{ width: pct(GET_COUNT.DTM) }"></span></div>
          </template>
          <template v-else><div class="sk sk--title"></div><div class="sk sk--count"></div><div class="sk sk--meter"></div></template>
        </v-card>
        <v-card class="royal-card royal--sub" elevation="0" :ripple="true" tabindex="0">
          <template v-if="countsLoaded">
            <div class="royal-title">{{ $t("Submitted card") }}</div>
            <div class="royal-count">{{ GET_COUNT.SUBMITTED | sum }}</div>
            <div class="royal-meter"><span :style="{ width: pct(GET_COUNT.SUBMITTED) }"></span></div>
          </template>
          <template v-else><div class="sk sk--title"></div><div class="sk sk--count"></div><div class="sk sk--meter"></div></template>
        </v-card>
        <v-card class="royal-card royal--rej" elevation="0" :ripple="true" tabindex="0">
          <template v-if="countsLoaded">
            <div class="royal-title">{{ $t("Rejected card") }}</div>
            <div class="royal-count">{{ GET_COUNT.REJECTED | sum }}</div>
            <div class="royal-meter"><span :style="{ width: pct(GET_COUNT.REJECTED) }"></span></div>
          </template>
          <template v-else><div class="sk sk--title"></div><div class="sk sk--count"></div><div class="sk sk--meter"></div></template>
        </v-card>
        <v-card class="royal-card royal--acc" elevation="0" :ripple="true" tabindex="0">
          <template v-if="countsLoaded">
            <div class="royal-title">{{ $t("Accepted card") }}</div>
            <div class="royal-count">{{ GET_COUNT.ACCEPTED | sum }}</div>
            <div class="royal-meter"><span :style="{ width: pct(GET_COUNT.ACCEPTED) }"></span></div>
          </template>
          <template v-else><div class="sk sk--title"></div><div class="sk sk--count"></div><div class="sk sk--meter"></div></template>
        </v-card>
        <v-card class="royal-card royal--pass" elevation="0" :ripple="true" tabindex="0">
          <template v-if="countsLoaded">
            <div class="royal-title">{{ $t("Passed card") }}</div>
            <div class="royal-count">{{ GET_COUNT.PASSED | sum }}</div>
            <div class="royal-meter"><span :style="{ width: pct(GET_COUNT.PASSED) }"></span></div>
          </template>
          <template v-else><div class="sk sk--title"></div><div class="sk sk--count"></div><div class="sk sk--meter"></div></template>
        </v-card>
        <v-card class="royal-card royal--rec" elevation="0" :ripple="true" tabindex="0">
          <template v-if="countsLoaded">
            <div class="royal-title">{{ $t("Tavsiya qilinganlar") }}</div>
            <div class="royal-count">{{ GET_COUNT.PASSED | sum }}</div>
            <div class="royal-meter"><span :style="{ width: pct(GET_COUNT.PASSED) }"></span></div>
          </template>
          <template v-else><div class="sk sk--title"></div><div class="sk sk--count"></div><div class="sk sk--meter"></div></template>
        </v-card>
        <v-card class="royal-card royal--exam" elevation="0" :ripple="true" tabindex="0">
          <template v-if="countsLoaded">
            <div class="royal-title">{{ $t("EXAMINED") }}</div>
            <div class="royal-count">{{ GET_COUNT.EXAMINED | sum }}</div>
            <div class="royal-meter"><span :style="{ width: pct(GET_COUNT.EXAMINED) }"></span></div>
          </template>
          <template v-else><div class="sk sk--title"></div><div class="sk sk--count"></div><div class="sk sk--meter"></div></template>
        </v-card>
        <v-card class="royal-card royal--tpass" elevation="0" :ripple="true" tabindex="0">
          <template v-if="countsLoaded">
            <div class="royal-title">{{ $t("TESTDAN O'TGAN") }}</div>
            <div class="royal-count">{{ GET_COUNT.EXAMINED_PASSED | sum }}</div>
            <div class="royal-meter"><span :style="{ width: pct(GET_COUNT.EXAMINED_PASSED) }"></span></div>
          </template>
          <template v-else><div class="sk sk--title"></div><div class="sk sk--count"></div><div class="sk sk--meter"></div></template>
        </v-card>
        <v-card class="royal-card royal--tfail" elevation="0" :ripple="true" tabindex="0">
          <template v-if="countsLoaded">
            <div class="royal-title">{{ $t("TESTDAN YIQILGAN") }}</div>
            <div class="royal-count">{{ GET_COUNT.EXAMINED_FAILED | sum }}</div>
            <div class="royal-meter"><span :style="{ width: pct(GET_COUNT.EXAMINED_FAILED) }"></span></div>
          </template>
          <template v-else><div class="sk sk--title"></div><div class="sk sk--count"></div><div class="sk sk--meter"></div></template>
        </v-card>
        <v-card class="royal-card royal--bfail" elevation="0" :ripple="true" tabindex="0">
          <template v-if="countsLoaded">
            <div class="royal-title">{{ $t("Bitrixga yuborilmaganlar") }}</div>
            <div class="royal-count">{{ GET_COUNT.BITRIX_FAILED | sum }}</div>
            <div class="royal-meter"><span :style="{ width: pct(GET_COUNT.BITRIX_FAILED) }"></span></div>
          </template>
          <template v-else><div class="sk sk--title"></div><div class="sk sk--count"></div><div class="sk sk--meter"></div></template>
        </v-card>
      </div>
    </v-container>

    <!-- ══════ TradingView-Style Chart ══════ -->
    <v-container v-if="isSupperAdmin || canRead" fluid class="royal-row">
      <div :class="['tv', { 'tv--collapsed': chartCollapsed }]">
        <!-- ── Unified Toolbar ── -->
        <div class="tv__toolbar">
          <div class="tv__toolbar-left">
            <!-- Symbol + Total -->
            <div class="tv__symbol-badge">
              <svg class="tv__symbol-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
            </div>

            <!-- Inline total + delta -->
            <span class="tv__inline-total">{{ formatNum(displayTotal) }}</span>
            <span v-if="isUp" class="tv__inline-delta tv__inline-delta--up">
              +{{ deltaAbs }}<template v-if="deltaPercent !== null"> (+{{ deltaPercent }}%)</template>
            </span>

            <div class="tv__sep"></div>

            <!-- Timeframes -->
            <div class="tv__tf-group">
              <button v-for="d in [7, 14, 30, 90]" :key="d"
                :class="['tv__tf-btn', chartDays === d && 'tv__tf-btn--active']"
                @click="setChartDays(d)">{{ d }}D</button>
            </div>

            <div class="tv__sep"></div>

            <!-- Source selector -->
            <div class="tv__src-group">
              <button :class="['tv__src-btn', chartSource === 'ALL' && 'tv__src-btn--active']" @click="setChartSource('ALL')">
                {{ $t("All") }}
              </button>
              <button :class="['tv__src-btn', chartSource === 'ADMISSION' && 'tv__src-btn--active tv__src-btn--adm']" @click="setChartSource('ADMISSION')">
                <span class="tv__dot tv__dot--adm"></span>Admission
                <span class="tv__src-count">{{ formatNum(admissionTotal) }}</span>
              </button>
              <button :class="['tv__src-btn', chartSource === 'QABUL' && 'tv__src-btn--active tv__src-btn--qab']" @click="setChartSource('QABUL')">
                <span class="tv__dot tv__dot--qab"></span>Qabul
                <span class="tv__src-count">{{ formatNum(qabulTotal) }}</span>
              </button>
              <button :class="['tv__src-btn', chartSource === 'LEAD' && 'tv__src-btn--active tv__src-btn--lead']" @click="setChartSource('LEAD')">
                <span class="tv__dot tv__dot--lead"></span>Lead
                <span class="tv__src-count">{{ formatNum(leadTotal) }}</span>
              </button>
              <button :class="['tv__src-btn', chartSource === 'REFERRAL' && 'tv__src-btn--active tv__src-btn--ref']" @click="setChartSource('REFERRAL')">
                <span class="tv__dot tv__dot--ref"></span>Qabul xodimlari
                <span class="tv__src-count">{{ formatNum(referralTotal) }}</span>
              </button>
              <button :class="['tv__src-btn', chartSource === 'CALL_MARKAZ' && 'tv__src-btn--active tv__src-btn--call']" @click="setChartSource('CALL_MARKAZ')">
                <span class="tv__dot tv__dot--call"></span>Call markaz
                <span class="tv__src-count">{{ formatNum(callMarkazTotal) }}</span>
              </button>
            </div>

            <span v-if="useLog" class="tv__log-badge" title="Logarifmik o'q: har bir to'r chizig'i = x10">log</span>
          </div>
          <div class="tv__toolbar-right">
            <button :class="['tv__type-btn', chartType === 'area' && 'tv__type-btn--active']" @click="chartType = 'area'" title="Area">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M1 12L5 7L8 9L15 3V12H1Z" fill="currentColor" opacity=".2"/><path d="M1 12L5 7L8 9L15 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <button :class="['tv__type-btn', chartType === 'line' && 'tv__type-btn--active']" @click="chartType = 'line'" title="Line">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M1 12L5 7L8 9L15 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <div class="tv__sep"></div>
            <button class="tv__type-btn tv__collapse" @click="toggleChartCollapsed" :title="chartCollapsed ? 'Expand' : 'Collapse'" :aria-expanded="String(!chartCollapsed)">
              <svg class="tv__collapse-icon" viewBox="0 0 128 128" width="14" height="14" aria-hidden="true"><path d="m64 92.5 50-50-7.1-7.1-17.1 17.2-25.8 25.8-25.8-25.8-17.1-17.1-7.1 7.1z"/></svg>
            </button>
          </div>
        </div>

        <!-- ── Chart Canvas ── -->
        <div class="tv__canvas" v-if="growthLoaded && cumulativeData.length">
          <svg
            ref="chartSvg"
            :viewBox="`0 0 ${W} ${H}`"
            preserveAspectRatio="none"
            class="tv__svg"
            @mousemove="onChartMove"
            @mouseleave="onChartLeave"
          >
            <defs>
              <linearGradient id="tvGradAdm" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="#2962ff" stop-opacity="0.28"/>
                <stop offset="60%" stop-color="#2962ff" stop-opacity="0.06"/>
                <stop offset="100%" stop-color="#2962ff" stop-opacity="0"/>
              </linearGradient>
              <linearGradient id="tvGradQab" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="#00c853" stop-opacity="0.28"/>
                <stop offset="60%" stop-color="#00c853" stop-opacity="0.06"/>
                <stop offset="100%" stop-color="#00c853" stop-opacity="0"/>
              </linearGradient>
              <linearGradient id="tvGradLead" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="#f59e0b" stop-opacity="0.28"/>
                <stop offset="60%" stop-color="#f59e0b" stop-opacity="0.06"/>
                <stop offset="100%" stop-color="#f59e0b" stop-opacity="0"/>
              </linearGradient>
              <linearGradient id="tvGradRef" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="#7c3aed" stop-opacity="0.28"/>
                <stop offset="60%" stop-color="#7c3aed" stop-opacity="0.06"/>
                <stop offset="100%" stop-color="#7c3aed" stop-opacity="0"/>
              </linearGradient>
              <linearGradient id="tvGradCall" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="#0d9488" stop-opacity="0.28"/>
                <stop offset="60%" stop-color="#0d9488" stop-opacity="0.06"/>
                <stop offset="100%" stop-color="#0d9488" stop-opacity="0"/>
              </linearGradient>
            </defs>

            <!-- Watermark removed -->

            <!-- Horizontal grid -->
            <g class="tv__grid">
              <line v-for="t in yTicks" :key="'g'+t.v"
                :x1="PL" :x2="PL + pW"
                :y1="t.y"
                :y2="t.y"/>
            </g>

            <!-- Right Y-axis labels -->
            <g class="tv__y-ax">
              <template v-for="t in yTicks">
                <text v-if="!endLabelYs.some((ly) => Math.abs(ly - t.y) < 11)"
                  :key="'y'+t.v"
                  :x="PL + pW + 8" :y="t.y + 4"
                  text-anchor="start">{{ t.label }}</text>
              </template>
            </g>

            <!-- Bottom X-axis labels -->
            <g class="tv__x-ax">
              <text v-for="l in xLabels" :key="'x'+l.i" :x="l.x" :y="PT + pH + 18" text-anchor="middle">{{ l.label }}</text>
            </g>

            <!-- Axis borders -->
            <line :x1="PL + pW" :x2="PL + pW" :y1="PT" :y2="PT + pH" class="tv__ax-border"/>
            <line :x1="PL" :x2="PL + pW" :y1="PT + pH" :y2="PT + pH" class="tv__ax-border"/>

            <!-- ── Series ── -->
            <!-- ALL: show all three, but only render the ones that have data -->
            <template v-if="chartSource === 'ALL'">
              <path v-if="chartType === 'area'" :d="admissionArea" fill="url(#tvGradAdm)" class="tv__area"/>
              <path v-if="chartType === 'area' && hasQabulData" :d="qabulArea" fill="url(#tvGradQab)" class="tv__area"/>
              <path v-if="chartType === 'area' && hasLeadData" :d="leadArea" fill="url(#tvGradLead)" class="tv__area"/>
              <path :d="admissionPath" fill="none" stroke="#2962ff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="tv__line"/>
              <path v-if="hasQabulData" :d="qabulPath" fill="none" stroke="#00c853" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="tv__line"/>
              <path v-if="hasLeadData" :d="leadPath" fill="none" stroke="#f59e0b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="tv__line"/>
            </template>
            <template v-if="chartSource === 'ADMISSION'">
              <path v-if="chartType === 'area'" :d="admissionArea" fill="url(#tvGradAdm)" class="tv__area"/>
              <path :d="admissionPath" fill="none" stroke="#2962ff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="tv__line"/>
            </template>
            <template v-if="chartSource === 'QABUL'">
              <path v-if="chartType === 'area'" :d="qabulArea" fill="url(#tvGradQab)" class="tv__area"/>
              <path :d="qabulPath" fill="none" stroke="#00c853" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="tv__line"/>
            </template>
            <template v-if="chartSource === 'LEAD'">
              <path v-if="chartType === 'area'" :d="leadArea" fill="url(#tvGradLead)" class="tv__area"/>
              <path :d="leadPath" fill="none" stroke="#f59e0b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="tv__line"/>
            </template>
            <template v-if="chartSource === 'REFERRAL'">
              <path v-if="chartType === 'area'" :d="referralArea" fill="url(#tvGradRef)" class="tv__area"/>
              <path :d="referralPath" fill="none" stroke="#7c3aed" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="tv__line"/>
            </template>
            <template v-if="chartSource === 'CALL_MARKAZ'">
              <path v-if="chartType === 'area'" :d="callMarkazArea" fill="url(#tvGradCall)" class="tv__area"/>
              <path :d="callMarkazPath" fill="none" stroke="#0d9488" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="tv__line"/>
            </template>

            <!-- Live price line (single-source only; ALL uses per-series end pills) -->
            <line
              v-if="chartSource !== 'ALL'"
              :x1="lastPoint.x" :x2="PL + pW"
              :y1="lastPoint.y" :y2="lastPoint.y"
              class="tv__price-line" :stroke="primaryColor"/>

            <!-- Live end pills + dots: exactly 1 in single-source, up to 3 (vertically dodged) in ALL -->
            <g v-for="e in endLabels" :key="'e'+e.key">
              <circle :cx="e.x" :cy="e.dotY" r="4" :fill="e.color" class="tv__live-dot"/>
              <circle :cx="e.x" :cy="e.dotY" r="4" :fill="e.color" class="tv__live-pulse"/>
              <rect :x="PL + pW + 1" :y="e.labelY - 10" width="58" height="20" rx="3" :fill="e.color"/>
              <text :x="PL + pW + 30" :y="e.labelY + 4" text-anchor="middle" class="tv__price-label-text" :fill="e.txt">{{ formatNum(e.value) }}</text>
            </g>

            <!-- ── Crosshair ── -->
            <g v-if="crosshair.show">
              <line :x1="tooltip.x" :x2="tooltip.x" :y1="PT" :y2="PT+pH" class="tv__cross-v"/>
              <line v-if="chartSource !== 'ALL'" :x1="PL" :x2="PL+pW" :y1="tooltip.y" :y2="tooltip.y" class="tv__cross-h"/>

              <!-- Time label on X axis -->
              <rect :x="tooltip.x - 32" :y="PT + pH + 2" width="64" height="18" rx="3" class="tv__axis-label-bg"/>
              <text :x="tooltip.x" :y="PT + pH + 14" text-anchor="middle" class="tv__axis-label-text">{{ fmtDateShort(tooltip.date) }}</text>

              <!-- Price label on Y axis (hide when near live price) -->
              <template v-if="chartSource !== 'ALL' && Math.abs(tooltip.y - lastPoint.y) > 22">
                <rect :x="PL + pW + 1" :y="tooltip.y - 9" width="58" height="18" rx="3" class="tv__axis-label-bg"/>
                <text :x="PL + pW + 30" :y="tooltip.y + 4" text-anchor="middle" class="tv__axis-label-text">{{ formatNum(tooltip.count) }}</text>
              </template>
            </g>

            <!-- Snap dots: one per drawn series at the hovered x -->
            <template v-if="tooltip.show">
              <circle v-for="(p, i) in tooltip.points" :key="'snap'+i"
                :cx="tooltip.x" :cy="p.y"
                r="5" :fill="p.color"
                stroke="var(--tv-bg)" stroke-width="2"
                class="tv__snap-dot"/>
            </template>
          </svg>

        </div>

        <!-- Skeleton -->
        <div v-else class="tv__skeleton">
          <div class="tv__sk-bar" v-for="n in 20" :key="n" :style="{ height: (20 + Math.random() * 60) + '%', animationDelay: (n * 0.05) + 's' }"></div>
        </div>

        <!-- Floating tooltip (outside canvas to avoid overflow:hidden clipping) -->
        <transition name="tv-fade">
          <div
            v-if="tooltip.show"
            class="tv__tip"
            :style="tipStyle"
          >
            <div class="tv__tip-header">
              <span class="tv__tip-date">{{ fmtDate(tooltip.date) }}</span>
            </div>
            <div class="tv__tip-body">
              <div class="tv__tip-row">
                <span class="tv__dot tv__dot--adm"></span>
                <span class="tv__tip-name">Admission</span>
                <span class="tv__tip-val">+{{ tooltip.dayAdmission }}</span>
              </div>
              <div class="tv__tip-row">
                <span class="tv__dot tv__dot--qab"></span>
                <span class="tv__tip-name">Qabul</span>
                <span class="tv__tip-val">+{{ tooltip.dayQabul }}</span>
              </div>
              <div class="tv__tip-row">
                <span class="tv__dot tv__dot--lead"></span>
                <span class="tv__tip-name">Lead</span>
                <span class="tv__tip-val">+{{ tooltip.dayLead || 0 }}</span>
              </div>
              <div class="tv__tip-footer">
                <span>{{ $t("All") }}</span>
                <span class="tv__tip-val">{{ formatNum(tooltip.count) }}</span>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </v-container>

    <!-- ── Tabs + Table ── -->
    <v-container fluid>
      <div class="tabs-panel">
        <v-tabs ref="tabsBar" v-model="tab" color="accent">
          <v-tab :disabled="isLoading" class="transform-none" v-text="$t('barcha ariza')"/>
          <!-- The counter pills read the SAME counts the tab lists are built from
               (GET_COUNT.COMPLETED / DTM / MAGISTR / SECOND_SPES are computed with each tab's own
               predicates on the backend), so a pill always matches the rows below it. -->
          <v-tab :disabled="isLoading" class="transform-none">
            {{ $t("toliq bolgan ariza") }}
            <span v-if="countsLoaded" class="tab-pill">{{ GET_COUNT.COMPLETED | sum }}</span>
          </v-tab>
          <!-- DTM arrivals. Index 2 here MUST stay aligned with index 2 of the three
               positional arrays in store/admin-applications.js. -->
          <v-tab :disabled="isLoading" class="transform-none">
            DTM
            <span v-if="countsLoaded" class="tab-pill">{{ GET_COUNT.DTM | sum }}</span>
          </v-tab>
          <!-- Hujjat turi = Magistratura (MASTER), Tavsiya/Qaytarilganlarsiz. Index 3 here MUST
               stay aligned with index 3 of the arrays in store/admin-applications.js. -->
          <v-tab :disabled="isLoading" class="transform-none">
            {{ $t("magistratura ariza") }}
            <span v-if="countsLoaded" class="tab-pill">{{ GET_COUNT.MAGISTR | sum }}</span>
          </v-tab>
          <!-- Hujjat turi = Ikkinchi mutaxassislik (SECOND_SPES), Tavsiya/Qaytarilganlarsiz. Index 4
               here MUST stay aligned with index 4 of the arrays in store/admin-applications.js. -->
          <v-tab :disabled="isLoading" class="transform-none">
            {{ $t("ikkinchi mutaxassislik ariza") }}
            <span v-if="countsLoaded" class="tab-pill">{{ GET_COUNT.SECOND_SPES | sum }}</span>
          </v-tab>
          <v-tab :disabled="isLoading" class="transform-none" v-text="$t('yangi ariza')"/>
          <v-tab :disabled="isLoading" class="transform-none" v-text="$t('qaytarilgan ariza')"/>
          <v-tab :disabled="isLoading" class="transform-none" v-text="$t('qabul qilingan ariza')"/>
          <v-tab :disabled="isLoading" class="transform-none" v-text="$t('oqishga kirgan ariza')"/>
          <v-tab :disabled="isLoading" class="transform-none" v-text="$t('EXAMINED')"/>
          <v-tab :disabled="isLoading" class="transform-none">{{ $t("TESTDAN O'TGAN") }}</v-tab>
          <v-tab :disabled="isLoading" class="transform-none">{{ $t("TESTDAN YIQILGAN") }}</v-tab>
          <v-tab :disabled="isLoading" class="transform-none">{{ $t("Bitrixga yuborilmaganlar").toUpperCase() }}</v-tab>
          <!-- DTM xlsx importidan kelgan arizalar. Index 13 here MUST stay aligned with index 13 of
               the three positional arrays in store/admin-applications.js. -->
          <v-tab :disabled="isLoading" class="transform-none">{{ $t("EXCEL ORQALI YUKLANGAN") }}</v-tab>
        </v-tabs>
        <!-- The applications list is deliberately NOT bound to the chart's source toggle. Coupling them
             let a selected/persisted chart source silently filter the table and hide real applicants
             (e.g. self-registered QABUL drafts that haven't picked a speciality yet). The chart source
             now drives the analytics graph only; the list always shows ALL applicants. Source filtering,
             if needed, belongs in the filter panel. -->
        <admin-applications-list @loading="(v) => (isLoading = v)" v-model="tab"/>
      </div>
    </v-container>
  </div>
</template>

<style lang="scss">
/* ══ Tokens ══ */
$radius: 14px;
$line: #e6e8ec;
$ink: #0f172a;
$muted: #667085;
$royal-blue: #3451e6;
$royal-amber: #e67e22;
$imperial-red: #c81e1e;
$royal-purple: #8844b6;
$royal-rose: #d6336c;
$emerald: #148b4b;
$teal: #0d9488;
$crimson: #be123c;
$royal-indigo: #4f46e5;
$royal-cyan: #0e7490;

/* ══ Cards ══ */
.ux-applications {
  min-height: 100vh;
  .royal-row { padding: 4px 12px !important; }
  .royal-grid {
    display: grid !important;
    grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
    gap: 10px !important;
    @media (max-width: 960px)  { grid-template-columns: repeat(2, 1fr) !important; }
    @media (max-width: 560px)  { grid-template-columns: 1fr !important; }
  }
  .royal-card.v-card {
    position: relative; overflow: hidden; cursor: pointer;
    background: #fff !important; border: 1px solid $line !important;
    border-radius: $radius !important; padding: 16px !important;
    min-height: 92px !important; max-height: unset !important; box-shadow: none !important;
    display: flex !important; flex-direction: column !important; justify-content: center !important; gap: 8px !important;
    transition: border-color .18s ease, transform .18s ease; outline: none;
    &::before { content: ""; position: absolute; left: 0; right: 0; top: 0; height: 4px; background: transparent; }
    &:hover { transform: translateY(-1px); border-color: #d7d9df !important; }
    &:active { transform: translateY(0); }
    &:focus { outline: none; }
    &::after { content: ""; position: absolute; inset: 0; background: linear-gradient(90deg, transparent, rgba(0,0,0,.03), transparent); transform: translateX(-100%); pointer-events: none; }
    &:hover::after { animation: wave 1200ms ease; }
    animation: slideUpFade .38s ease both;
  }
  .royal-card:nth-child(1){ animation-delay: 40ms; }
  .royal-card:nth-child(2){ animation-delay: 80ms; }
  .royal-card:nth-child(3){ animation-delay: 120ms; }
  .royal-card:nth-child(4){ animation-delay: 160ms; }
  .royal-card:nth-child(5){ animation-delay: 200ms; }
  .royal-card:nth-child(6){ animation-delay: 240ms; }
  .royal-card:nth-child(7){ animation-delay: 280ms; }
  .royal-card:nth-child(8){ animation-delay: 320ms; }
  .royal-card:nth-child(9){ animation-delay: 360ms; }
  .royal-title { color: $muted !important; font-size: 11px !important; line-height: 1 !important; letter-spacing: .08em; text-transform: uppercase; font-weight: 700 !important; }
  .royal-count { color: $ink !important; font-weight: 900 !important; font-size: clamp(24px, 3.6vw, 36px) !important; line-height: 1 !important; letter-spacing: -0.2px; font-variant-numeric: tabular-nums lining-nums; }
  .royal-meter { height: 6px !important; margin-top: 6px !important; border-radius: 999px; background: #f1f3f5; overflow: hidden; > span { display: block; height: 100%; width: 0%; transition: width .45s cubic-bezier(.2,.8,.2,1); } }
  .royal-card.royal--all  { &::before { background: $royal-blue; } .royal-meter > span { background: $royal-blue; } }
  .royal-card.royal--sub  { &::before { background: $royal-amber; } .royal-meter > span { background: $royal-amber; } }
  .royal-card.royal--rej  { &::before { background: $imperial-red; } .royal-meter > span { background: $imperial-red; } }
  .royal-card.royal--acc  { &::before { background: $royal-purple; } .royal-meter > span { background: $royal-purple; } }
  .royal-card.royal--exam { &::before { background: $royal-rose; } .royal-meter > span { background: $royal-rose; } }
  .royal-card.royal--pass { &::before { background: $emerald; } .royal-meter > span { background: $emerald; } }
  .royal-card.royal--tpass { &::before { background: $teal; } .royal-meter > span { background: $teal; } }
  .royal-card.royal--tfail { &::before { background: $crimson; } .royal-meter > span { background: $crimson; } }
  .royal-card.royal--bfail { &::before { background: $imperial-red; } .royal-meter > span { background: $imperial-red; } }
  .royal-card.royal--rec  { &::before { background: $royal-indigo; } .royal-meter > span { background: $royal-indigo; } }
  .royal-card.royal--dtm  { &::before { background: $royal-cyan; } .royal-meter > span { background: $royal-cyan; } }
  .tabs-panel { background: #fff; border: 1px solid $line; border-radius: $radius; overflow: hidden; }

  /* Counter pill inside a tab label: "TO'LIQ BO'LGAN  1 234". Neutral until the tab is
     active, then it picks up the accent so it reads as part of the selected tab. */
  .tabs-panel .v-tab .tab-pill {
    display: inline-flex; align-items: center; justify-content: center;
    margin-left: 6px; padding: 0 7px; min-width: 20px; height: 19px;
    border-radius: 999px; background: #eef0f4; color: $muted;
    font-size: 11px; font-weight: 800; line-height: 1;
    /* v-tab sets letter-spacing: .0892em — inherited, it adds a trailing gap after the
       last digit and the number no longer sits centred in the pill. */
    letter-spacing: 0;
    font-variant-numeric: tabular-nums lining-nums;
    transition: background-color .18s ease, color .18s ease;
  }
  .tabs-panel .v-tab--active .tab-pill { background: rgba(52, 81, 230, .12); color: $royal-blue; }
}

/* ══════════════════════════════════════════════════════════════
   TradingView Chart — Light Mode
   ══════════════════════════════════════════════════════════════ */
.tv {
  --tv-bg: #ffffff;
  --tv-border: #e0e3eb;
  --tv-toolbar-bg: #f8f9fd;
  --tv-text: #131722;
  --tv-text-muted: #787b86;
  --tv-grid: #f0f3fa;
  --tv-axis-text: #787b86;
  --tv-cross: #9598a1;
  --tv-axis-label-bg: #4c525e;
  --tv-axis-label-text: #ffffff;
  --tv-tip-bg: #ffffff;
  --tv-tip-border: #e0e3eb;
  --tv-tip-text: #131722;
  --tv-tip-muted: #787b86;
  --tv-watermark: rgba(0,0,0,.03);
  --tv-hover: rgba(0,0,0,.04);
  --tv-btn-active-bg: #e3e6ef;
  --tv-sep: #e0e3eb;

  background: var(--tv-bg);
  border: 1px solid var(--tv-border);
  border-radius: 8px;
  position: relative;
  animation: slideUpFade .38s ease both;
  animation-delay: 360ms;
  font-family: -apple-system, BlinkMacSystemFont, 'Trebuchet MS', Roboto, Ubuntu, sans-serif;
}

/* ── Toolbar ── */
.tv__toolbar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 6px 12px;
  background: var(--tv-toolbar-bg);
  border-bottom: 1px solid var(--tv-border);
  border-radius: 8px 8px 0 0;
  gap: 8px; flex-wrap: wrap; min-height: 40px;
  position: relative; z-index: 1;
}
.tv__toolbar-left { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.tv__toolbar-right { display: flex; align-items: center; gap: 2px; }

.tv__symbol-badge {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 10px; border-radius: 4px;
  font-size: 13px; font-weight: 700; color: var(--tv-text);
}
.tv__symbol-icon { color: #2962ff; flex-shrink: 0; }

.tv__inline-total {
  font-size: 13px; font-weight: 700; color: var(--tv-text);
  font-variant-numeric: tabular-nums;
}
.tv__inline-delta {
  font-size: 12px; font-weight: 600; font-variant-numeric: tabular-nums;
  &--up { color: #089981; }
  &--down { color: #f23645; }
}

.tv__sep { width: 1px; height: 20px; background: var(--tv-sep); flex-shrink: 0; }

.tv__tf-group, .tv__src-group { display: flex; gap: 1px; }

.tv__tf-btn, .tv__src-btn {
  padding: 4px 10px; border: none; background: none; border-radius: 4px;
  font-size: 12px; font-weight: 600; color: var(--tv-text-muted); cursor: pointer;
  transition: all .1s ease; white-space: nowrap;
  display: inline-flex; align-items: center; gap: 4px;
  &:hover { background: var(--tv-hover); color: var(--tv-text); }
}
.tv__tf-btn--active, .tv__src-btn--active {
  background: var(--tv-btn-active-bg); color: var(--tv-text);
}
.tv__src-btn--active.tv__src-btn--adm { color: #2962ff; }
.tv__src-btn--active.tv__src-btn--qab { color: #00c853; }
.tv__src-btn--active.tv__src-btn--lead { color: #f59e0b; }
.tv__src-btn--active.tv__src-btn--ref { color: #7c3aed; }
.tv__src-btn--active.tv__src-btn--call { color: #0d9488; }

.tv__src-count {
  font-size: 11px; font-weight: 700; opacity: .6;
  font-variant-numeric: tabular-nums;
  margin-left: 2px;
}

.tv__type-btn {
  padding: 5px 7px; border: none; background: none; border-radius: 4px;
  color: var(--tv-text-muted); cursor: pointer; transition: all .1s ease;
  display: flex; align-items: center;
  &:hover { background: var(--tv-hover); color: var(--tv-text); }
  &--active { color: var(--tv-text); background: var(--tv-btn-active-bg); }
}

/* ── Collapse toggle ── */
.tv__collapse-icon {
  width: 14px; height: 14px; fill: currentColor;
  transition: transform .22s ease; transform: rotate(180deg);
}
.tv--collapsed .tv__collapse-icon { transform: rotate(0deg); }
.tv--collapsed .tv__canvas,
.tv--collapsed .tv__skeleton { display: none; }
.tv--collapsed .tv__toolbar { border-bottom: none; border-radius: 8px; }

.tv__dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; display: inline-block; }
.tv__dot--adm { background: #2962ff; }
.tv__dot--qab { background: #00c853; }
.tv__dot--lead { background: #f59e0b; }
.tv__dot--ref { background: #7c3aed; }
.tv__dot--call { background: #0d9488; }

/* ── Chart Canvas ── */
.tv__canvas { position: relative; width: 100%; aspect-ratio: 3 / 1; min-height: 240px; background: var(--tv-bg); border-radius: 0 0 8px 8px; overflow: hidden; }
.tv__svg { width: 100%; height: 100%; cursor: crosshair; }

.tv__watermark {
  font-size: 36px; font-weight: 800; fill: var(--tv-watermark);
  letter-spacing: 3px; user-select: none; pointer-events: none;
}

.tv__grid line { stroke: var(--tv-grid); stroke-width: 1; }
.tv__ax-border { stroke: var(--tv-border); stroke-width: 1; }

.tv__y-ax text { font-size: 11px; fill: var(--tv-axis-text); font-weight: 400; font-variant-numeric: tabular-nums; }
.tv__x-ax text { font-size: 11px; fill: var(--tv-axis-text); font-weight: 400; }

.tv__area { transition: d .2s ease; }
.tv__line { transition: d .2s ease; }

/* Live price line */
.tv__price-line { stroke-width: 1; stroke-dasharray: 3,3; opacity: .8; }
.tv__price-label-text { font-size: 11px; fill: #fff; font-weight: 700; font-variant-numeric: tabular-nums; }
.tv__log-badge {
  font-size: 10px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
  border-radius: 4px; padding: 2px 6px; align-self: center;
  color: var(--tv-text-muted); background: rgba(120, 123, 134, .16);
}
.theme--dark .tv__log-badge { color: #aeb4c0; background: rgba(174, 180, 192, .14); }

.tv__live-dot { }
.tv__live-pulse { animation: tvPulse 2s ease-in-out infinite; }

/* Crosshair */
.tv__cross-v { stroke: var(--tv-cross); stroke-width: 1; stroke-dasharray: 3,3; }
.tv__cross-h { stroke: var(--tv-cross); stroke-width: 1; stroke-dasharray: 3,3; }
.tv__axis-label-bg { fill: var(--tv-axis-label-bg); }
.tv__axis-label-text { font-size: 11px; fill: var(--tv-axis-label-text); font-weight: 500; font-variant-numeric: tabular-nums; }

.tv__snap-dot { pointer-events: none; }

/* ── Tooltip ── */
.tv__tip {
  position: absolute;
  background: var(--tv-tip-bg);
  border: 1px solid var(--tv-tip-border);
  border-radius: 6px; padding: 0;
  pointer-events: none; z-index: 100;
  box-shadow: 0 4px 16px rgba(0,0,0,.12);
  min-width: 170px; font-size: 12px;
  overflow: hidden;
}
.tv__tip-header {
  padding: 8px 12px;
  background: var(--tv-toolbar-bg);
  border-bottom: 1px solid var(--tv-tip-border);
}
.tv__tip-date { font-weight: 700; font-size: 12px; color: var(--tv-text); }
.tv__tip-body { padding: 8px 12px; }
.tv__tip-row {
  display: flex; align-items: center; gap: 6px; padding: 3px 0;
  color: var(--tv-text-muted); font-weight: 500;
}
.tv__tip-name { flex: 1; }
.tv__tip-val { font-weight: 700; color: var(--tv-text); font-variant-numeric: tabular-nums; }
.tv__tip-footer {
  display: flex; justify-content: space-between; align-items: center;
  border-top: 1px solid var(--tv-tip-border);
  margin-top: 4px; padding-top: 6px;
  font-weight: 600; color: var(--tv-text-muted);
}

.tv-fade-enter-active, .tv-fade-leave-active { transition: opacity .1s ease; }
.tv-fade-enter, .tv-fade-leave-to { opacity: 0; }

/* ── Skeleton ── */
.tv__skeleton {
  display: flex; align-items: flex-end; justify-content: center;
  height: 240px; gap: 3px; padding: 40px 60px 40px 12px;
  background: var(--tv-bg);
}
.tv__sk-bar {
  flex: 1; min-width: 3px; border-radius: 2px 2px 0 0;
  background: var(--tv-grid);
  animation: tvSkPulse 1.5s ease infinite;
}

@keyframes tvPulse {
  0%, 100% { opacity: 1; r: 4; }
  50% { opacity: .3; r: 8; }
}
@keyframes tvSkPulse {
  0%, 100% { opacity: .4; }
  50% { opacity: .8; }
}

/* ══════════════════════════════════════════════════════════════
   TradingView Chart — Dark Mode
   ══════════════════════════════════════════════════════════════ */
.theme--dark .tv {
  --tv-bg: #131722;
  --tv-border: #1e222d;
  --tv-toolbar-bg: #1e222d;
  --tv-text: #d1d4dc;
  --tv-text-muted: #787b86;
  --tv-grid: #1e222d;
  --tv-axis-text: #787b86;
  --tv-cross: #555960;
  --tv-axis-label-bg: #363a45;
  --tv-axis-label-text: #d1d4dc;
  --tv-tip-bg: #1e222d;
  --tv-tip-border: #2a2e39;
  --tv-tip-text: #d1d4dc;
  --tv-tip-muted: #787b86;
  --tv-watermark: rgba(255,255,255,.03);
  --tv-hover: rgba(255,255,255,.06);
  --tv-btn-active-bg: #2a2e39;
  --tv-sep: #2a2e39;
}

/* ══ Animations ══ */
@keyframes wave { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
@keyframes slideUpFade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
@media (prefers-reduced-motion: reduce) { .ux-applications .royal-card, .ux-applications .royal-meter > span { transition: none !important; animation: none !important; } }

/* ══ Filter button override ══ */
.ux-applications button.filter-btn.v-btn {
  display: inline-flex !important; align-items: center !important; justify-content: center !important;
  background-color: #f5f5f5 !important; color: #f5f5f5 !important;
  width: 40px !important; height: 40px !important; min-width: 40px !important; max-width: 40px !important;
  padding: 0 !important; border-radius: 50% !important; border: 1px solid #d0d5dd !important; cursor: pointer; transition: all 0.2s ease; position: relative;
}

/* ══ Dark Mode (cards + table) ══ */
.theme--dark .ux-applications {
  .royal-card.v-card {
    background: #1a1b23 !important; border-color: #2a2c3a !important;
    &:hover { border-color: #33354a !important; }
    &::after { background: linear-gradient(90deg, transparent, rgba(255,255,255,.02), transparent) !important; }
  }
  .royal-title { color: #8b8d9e !important; }
  .royal-count { color: #e4e5eb !important; }
  .royal-meter { background: #2a2c3a !important; }
  .tabs-panel { background: #1a1b23; border-color: #2a2c3a; }
  .tabs-panel .v-tab .tab-pill { background: #2a2c3a; color: #8b8d9e; }
  .tabs-panel .v-tab--active .tab-pill { background: rgba(96, 125, 255, .18); color: #93a5ff; }
}

@media (max-width: 960px) {
  .tv__tf-group, .tv__src-group { flex-wrap: wrap; }
}
@media (max-width: 600px) {
  .tv__canvas { min-height: 160px; aspect-ratio: 16 / 10; }
  .tv__tip { min-width: 140px; max-width: 70vw; }
}
</style>
